import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth-context.js";
import type { Permission } from "../types/api.js";
import type { ReactNode } from "react";

/** Landing on a route your role cannot open shows a clear explanation and somewhere to go -
 * never a blank screen and never an endless spinner. Hiding a nav item is a courtesy; the API
 * refuses the request regardless. */
export function RequirePermission({
  permission,
  children,
}: {
  permission: Permission;
  children: ReactNode;
}) {
  const { can, principal } = useAuth();
  if (can(permission)) return <>{children}</>;

  const somewhere = can("view_doors")
    ? "/doors"
    : can("view_status")
      ? "/alerts"
      : can("view_audit")
        ? "/audit"
        : null;

  return (
    <>
      <h1 id="page-heading" tabIndex={-1}>
        You cannot open this screen
      </h1>
      <div className="banner banner-warning">
        <span className="banner-icon" aria-hidden="true">
          ⚠
        </span>
        <div className="banner-body">
          Your role (<code>{principal?.role}</code>) does not have the <code>{permission}</code>{" "}
          permission, so this screen is not available to you. If you need it, ask a super_admin to
          change your role.
        </div>
      </div>
      {somewhere ? (
        <p>
          <Link to={somewhere}>Go to a screen you can use</Link>
        </p>
      ) : (
        <p>
          This credential has no screens in this console. It can be used against the API directly,
          but there is nothing here for it to show.
        </p>
      )}
    </>
  );
}
